import React, {useCallback, useContext, useLayoutEffect, useState} from "react";
import {OAuthManagerContext} from "./context";
import {useCheckAuth} from "./useCheckAuth";
import {ApolloClient, ApolloProvider, HttpLink, InMemoryCache, InMemoryCacheConfig} from "@apollo/client";
import {setContext} from "@apollo/client/link/context";
import {LoadingSpinner} from "../../../common";
import {CurrentUser} from "../CurrentUser";
import {onError} from "@apollo/client/link/error";
import {useMsal} from "@azure/msal-react";
import { AzureTokenManager } from "../../manager/AzureTokenManager";
import {TokenProvider} from "../TokenContext";

const APOLLO_IN_MEMORY_CACHE_CONFIG: InMemoryCacheConfig = {
    typePolicies: {
        Offer: {
            keyFields: ['id'],
        },
        Referral: {
            keyFields: ['id'],
        },
        ProviderUserInfoDTO: {
            keyFields: ['oktaId'],
        }
    }
};

const useAuthenticatedApolloClientAzure = (
    isAuthenticated: boolean,
    token: string | null,
    providerGuid?: string
    ): ApolloClient<any> | null => {
    const [apolloInstance, setApolloInstance] = useState<ApolloClient<any> | null>(null);
    const {instance} = useMsal();
    const [tokenManager] = useState(() => new AzureTokenManager(instance));


    const setUpApolloClient = useCallback(() => {
        if (isAuthenticated && token) {
            if (apolloInstance === null) {
                const errorLink = onError(({networkError}) => {
                    // Handle token expire with redirect lock to prevent infinity redirect issue
                    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
                        tokenManager.loginRedirect();
                    }
                });
                
                // Helper function to create headers with authorization and optional provider GUID
                const createHeaders = (headers: Record<string, string>, authToken: string | null) => {
                    // Use proper type with index signature to allow any string key
                    const updatedHeaders: Record<string, string> = {
                        ...headers,
                        Authorization: authToken ? `Bearer ${authToken}` : ""
                    };
                    
                    if (providerGuid) {
                        updatedHeaders['Provider-Guid'] = providerGuid;
                    }
                    
                    return { headers: updatedHeaders };
                };
                
                // Create auth link that checks token expiration before each request
                const authLink = setContext(async (_, {headers}) => {
                    // Check if token is about to expire
                    if (await tokenManager.isTokenExpiring()) {
                        try {
                            // Try to silently refresh the token
                            const newToken = await tokenManager.refreshToken();
                            
                            // Use the new token from the response
                            return createHeaders(headers, newToken);
                        } catch (error) {
                            console.error('[Apollo Request] Silent token refresh failed:', error);
                            // If silent refresh fails and we're not in the redirect lock period
                            if (!tokenManager.isRedirectLocked()) {
                                tokenManager.loginRedirect();
                            } else {
                                return createHeaders(headers, token);
                            }
                        }
                    }
                    
                    // Token is still valid, use it
                    return createHeaders(headers, token);
                });
                
                setApolloInstance(new ApolloClient({
                    link: errorLink.concat(authLink.concat(new HttpLink({uri: `${process.env.REACT_APP_API_URL}/api/graphql`}))),
                    cache: new InMemoryCache(APOLLO_IN_MEMORY_CACHE_CONFIG),
                }));
            }
        }
    }, [apolloInstance, tokenManager, isAuthenticated, providerGuid, token]);

    useLayoutEffect(() => {
        setUpApolloClient();
    }, [setUpApolloClient]);

    return apolloInstance;
}

// Token expiration is checked before each Apollo request

export const AzureAuthenticatedEnvironment = ({children, providerGuid}: { children: React.ReactNode, providerGuid?: string }) => {
    const oauthManager = useContext(OAuthManagerContext)!;
    const {checking, isAuthenticated, token} = useCheckAuth(oauthManager);
    const apolloInstance = useAuthenticatedApolloClientAzure(isAuthenticated, token, providerGuid);

    if (checking || apolloInstance === null) {
        return <LoadingSpinner text='Checking your session' centerScreen/>;
    }


    return (
        <ApolloProvider client={apolloInstance}>
            <TokenProvider token={token}>
                <CurrentUser>{children}</CurrentUser>
            </TokenProvider>
        </ApolloProvider>
    );
}